import { format } from "date-fns"

import { useLocale } from "@/i18n"
import { getActiveLocale } from "@/i18n/active-locale"
import { getDateFnsLocale } from "@/shared/lib/dates/date-fns-locale"

/**
 * Canonical date display for the whole app. Use this anywhere a date/time is shown to the user.
 *
 * - `"date"`     → date only            · en: "2 Jun 2026"        · fr: "2 juin 2026"
 * - `"datetime"` → date + time + tz     · en: "2 Jun 2026, 15:30 (GMT+2)" · fr: "2 juin 2026, 15:30 (GMT+2)"
 *
 * Time is rendered in the viewer's local timezone and the offset is always shown, so a
 * timestamp is never ambiguous. Locale-aware (en / fr) via the shared date-fns locale.
 */
export type DateDisplayVariant = "date" | "datetime"

const PATTERNS: Record<DateDisplayVariant, string> = {
  date: "d MMM yyyy",
  datetime: "d MMM yyyy, HH:mm (zzz)",
}

/** Normalize locale/runtime-dependent GMT labels to one product-wide form: UTC±HH:MM. */
function normalizeTimezoneLabel(value: string): string {
  return value.replace(/\((?:GMT|UTC)([+-])(\d{1,2})(?::(\d{2}))?\)$/u, (_, sign, hours, minutes) =>
    `(UTC${sign}${String(hours).padStart(2, "0")}:${minutes ?? "00"})`,
  ).replace(/\((?:GMT|UTC)\)$/u, "(UTC+00:00)")
}

export interface FormatDateOptions {
  /** "en" | "fr". Defaults to the active app locale; pass explicitly only to override. */
  locale?: string
  /** date only (default) or date + time + timezone. */
  variant?: DateDisplayVariant
  /** IANA timezone for branch-owned dates. Omit only for viewer-local UI. */
  timeZone?: string
  /** Returned when the value is missing or unparseable. Defaults to "—". */
  fallback?: string
}

export function formatDate(
  value: string | number | Date | null | undefined,
  options: FormatDateOptions = {},
): string {
  const { locale = getActiveLocale(), variant = "date", timeZone, fallback = "—" } = options
  if (value === null || value === undefined || value === "") return fallback
  const date = value instanceof Date ? value : new Date(value)
  if (Number.isNaN(date.getTime())) return fallback
  if (variant === "datetime" && timeZone) {
    try {
      const formatted = new Intl.DateTimeFormat(locale, {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hourCycle: "h23",
        timeZone,
      }).format(date)
      const offsetPart = new Intl.DateTimeFormat("en", {
        hour: "2-digit",
        timeZone,
        timeZoneName: "longOffset",
      }).formatToParts(date).find((part) => part.type === "timeZoneName")?.value ?? "GMT"
      const offset = offsetPart === "GMT" ? "+00:00" : offsetPart.replace(/^GMT/u, "")
      return `${formatted} (UTC${offset})`
    } catch {
      // Invalid stored zones must not break a whole table; viewer-local remains a safe visible fallback.
    }
  }
  return normalizeTimezoneLabel(format(date, PATTERNS[variant], { locale: getDateFnsLocale(locale) }))
}

/**
 * Hook bound to the active app locale. Returns a `formatDate(value, { variant }?)` function.
 * `const formatDate = useFormatDate(); formatDate(startDate); formatDate(createdAt, { variant: "datetime" })`
 */
export function useFormatDate() {
  const locale = useLocale()
  return (value: string | number | Date | null | undefined, options: Omit<FormatDateOptions, "locale"> = {}) =>
    formatDate(value, { ...options, locale })
}
